import React, { useState, useEffect } from "react";

import { Edit, Plus } from "icons";

import providerManagerDashboardApi from "apis/providerManagerDashboard";
import providerManagerEntitiesApi from "apis/providerManagerEntity";
import ProviderManagerTemplatesApi from "apis/providerManagerTemplate";
import {
  Accordion,
  Button,
  Dropdown,
  SubHeader,
  Table,
  PageLoader,
  Modal,
  Typography,
} from "clearsense-ui/components";
import PermissibleWrapper from "components/Common/PermissibleWrapper";
import { useUserState } from "contexts/user";
import { getPermissions } from "utils/roleBasedAccess";

import { TEMPLATE_COLUMNS, ENTITIES_COLUMN } from "./constants";
import CreateEntity from "./CreateEntity";
import CreateTemplate from "./CreateTemplate";
import CreateAssociation from "./CreateTemplate/Association";
import NPIRecordsList from "./NPIRecordsList";
import PrimaryTabs from "./PrimaryTabs";
import SearchNPI from "./SearchNPI";

const ProviderManager = ({ features }) => {
  const [isCreateTemplate, setIsCreateTemplate] = useState(false);
  const [searchParams, setSearchParams] = useState({});
  const [data, setData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isNPILoading, setIsNPILoading] = useState(false);
  const [npiRecords, setNpiRecords] = useState([]);
  const [isViewMode, setIsViewMode] = useState(false);
  const [currentEntityID, setCurrentEntityID] = useState("");
  const [currentEntityItemID, setCurrentEntityItemID] = useState("");
  const [editRecordId, setEditRecordId] = useState();
  const [providerManagerTemplate, setProviderManagerTemplate] = useState();
  const [
    isProviderManagerTemplateLoading,
    setIsProviderManagerTemplateLoading,
  ] = useState(false);
  const [entities, setEntities] = useState([]);
  const [isCreateAssociationTemplate, setIsCreateAssociationTemplate] =
    useState(false);
  const [isTemplateDetailsModal, setIsTemplateDetailsModal] = useState(false);
  const [editAssociationDetails, setEditAssociationDetails] = useState();
  const [currentNPIRecord, setCurrentNPIRecord] = useState();
  const [isEntitiesLoading, setIsEntitiesLoading] = useState(false);
  const [isCreateEntity, setIsCreateEntity] = useState(false);
  const [editEntityId, setEditEntityId] = useState();
  const user = useUserState();
  const userPermissions = getPermissions(features, user?.role);

  const fetchEntities = async () => {
    try {
      setIsEntitiesLoading(true);
      const { data } = await providerManagerEntitiesApi.fetch();
      setEntities(data.entities);
    } catch (err) {
      logger.log(err);
    } finally {
      setIsEntitiesLoading(false);
    }
  };

  const handleSearch = async values => {
    setSearchParams({ provider_manager_dashboard: { npi: values.npi } });
    setIsNPILoading(true);
    try {
      const { data } = await providerManagerDashboardApi.search({
        npi_number: values.npi,
      });
      setNpiRecords(data.primary);
    } catch (err) {
      logger.log(err);
    } finally {
      setIsNPILoading(false);
    }
  };

  const handleEditNPI = async ({
    npi,
    cdt_run_date,
    entity_id,
    entity_item_id,
  }) => {
    try {
      setIsLoading(true);
      setSearchParams({
        provider_manager_dashboard: {
          npi,
          cdt_run_date,
        },
      });
      const { data } = await providerManagerDashboardApi.show(entity_item_id);
      setData(JSON.parse(data.npi_data.value));
      setCurrentNPIRecord(data.npi_data);
      setCurrentEntityID(entity_id);
      setCurrentEntityItemID(entity_item_id);
    } catch (error) {
      logger.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const getProviderManagerTemplates = async () => {
    try {
      setIsProviderManagerTemplateLoading(true);
      const { data } = await ProviderManagerTemplatesApi.fetch();
      setProviderManagerTemplate(data);
    } catch (error) {
      logger.error(error);
    } finally {
      setIsProviderManagerTemplateLoading(false);
    }
  };

  const handleEntityEdit = id => {
    setEditEntityId(id);
    setIsCreateEntity(true);
  };

  const handleViewMoreDetails = id => {
    setEditRecordId(id);
    setIsTemplateDetailsModal(true);
  };

  useEffect(() => {
    getProviderManagerTemplates();
  }, []);

  useEffect(
    () => isCreateEntity === false && fetchEntities(),
    [isCreateEntity]
  );

  return (
    <div className="cs-ui-container px-8 space-y-8">
      <SubHeader
        breadcrumbs={[{ label: "Provider Manager", link: "#" }]}
        actionBlock={
          <PermissibleWrapper
            currentPermissions={userPermissions}
            acceptablePermissions={"config"}
          >
            <Dropdown customTarget={<Button label="Create" icon={Plus} />}>
              <li onClick={() => setIsCreateTemplate(true)}>Template</li>
              <li onClick={() => setIsCreateAssociationTemplate(true)}>
                Association
              </li>
              <li onClick={() => setIsCreateEntity(true)}> Entity</li>
            </Dropdown>
          </PermissibleWrapper>
        }
        className="mt-8"
      />
      <PermissibleWrapper
        currentPermissions={userPermissions}
        acceptablePermissions="config"
      >
        <Accordion defaultActiveKey="1">
          <Accordion.Item title="Entities">
            <div className="max-h-80">
              <Table
                rowData={entities}
                isLoading={isEntitiesLoading}
                columnData={ENTITIES_COLUMN(handleEntityEdit)}
                fixedHeight={true}
                sticky={true}
              />
            </div>
          </Accordion.Item>
          <Accordion.Item title="Templates">
            <div className="max-h-80">
              <Table
                rowData={providerManagerTemplate}
                isLoading={isProviderManagerTemplateLoading}
                columnData={TEMPLATE_COLUMNS(handleViewMoreDetails)}
                fixedHeight={true}
                sticky={true}
              />
            </div>
          </Accordion.Item>
        </Accordion>
      </PermissibleWrapper>
      <SearchNPI isNPILoading={isNPILoading} handleSearch={handleSearch} />
      {!isNPILoading && searchParams?.provider_manager_dashboard?.npi && (
        <NPIRecordsList
          npiRecords={npiRecords}
          searchParams={searchParams}
          userPermissions={userPermissions}
          entities={entities}
          handleEditNPI={handleEditNPI}
          setIsViewMode={setIsViewMode}
          handleSearch={handleSearch}
        />
      )}
      {isLoading ? (
        <PageLoader />
      ) : (
        searchParams?.provider_manager_dashboard?.cdt_run_date && (
          <PrimaryTabs
            data={data}
            npiRecords={npiRecords}
            searchParams={searchParams}
            isViewMode={isViewMode}
            userPermissions={userPermissions}
            currentEntityID={currentEntityID}
            currentEntityItemID={currentEntityItemID}
            entities={entities}
            currentNPIRecord={currentNPIRecord}
            updateData={handleEditNPI}
            handleSearch={handleSearch}
          />
        )
      )}
      <CreateTemplate
        isCreateTemplate={isCreateTemplate}
        templateId={editRecordId}
        setIsCreateTemplate={setIsCreateTemplate}
        setEditRecordId={setEditRecordId}
        getProviderManagerTemplates={getProviderManagerTemplates}
      />
      <CreateAssociation
        isCreateAssociationTemplate={isCreateAssociationTemplate}
        setIsCreateAssociationTemplate={setIsCreateAssociationTemplate}
        editAssociationDetails={editAssociationDetails}
        setEditAssociationDetails={setEditAssociationDetails}
        setEditRecordId={setEditRecordId}
        getProviderManagerTemplates={getProviderManagerTemplates}
      />
      <CreateEntity
        isCreateEntity={isCreateEntity}
        setIsCreateEntity={setIsCreateEntity}
        editEntityId={editEntityId}
        setEditEntityId={setEditEntityId}
      />
      <Modal
        size="md"
        isOpen={isTemplateDetailsModal}
        onClose={() => setIsTemplateDetailsModal(false)}
      >
        <Modal.Header>
          <div className="flex justify-between">
            <Typography style="h3" type="bold">
              {
                providerManagerTemplate?.filter(
                  template => template.id === editRecordId
                )[0]?.name
              }
            </Typography>
            <Typography style="h3" type="bold">
              {
                providerManagerTemplate?.filter(
                  template => template.id === editRecordId
                )[0]?.entity
              }
            </Typography>
          </div>
        </Modal.Header>
        <Modal.Body>
          <div className="flex justify-between mt-5">
            <Typography style="h4" type="bold">
              Hierarchies
            </Typography>
            <Button
              label="Edit Hierarchies"
              style="secondary"
              icon={Edit}
              onClick={() => {
                setIsTemplateDetailsModal(false);
                setIsCreateTemplate(true);
              }}
            />
          </div>
          <div className="ml-4">
            {providerManagerTemplate
              ?.filter(template => template?.id === editRecordId)?.[0]
              ?.template?.hierarchies?.map(hierarchy => (
                <Typography
                  key={hierarchy.id}
                  style="large"
                  className="text-content-600 dark:text-white mb-4"
                >
                  {hierarchy.name}
                </Typography>
              ))}
          </div>
          <Typography style="h4" type="bold" className="mt-7">
            Associations
          </Typography>
          <div className="ml-4">
            {providerManagerTemplate
              ?.filter(template => template?.id === editRecordId)?.[0]
              ?.associations?.map(association => (
                <div
                  key={association.to}
                  className="flex justify-between items-center mb-4"
                >
                  <Typography
                    key={association.to}
                    style="large"
                    type="medium"
                    className="text-content-600 dark:text-white items-center"
                  >
                    {
                      providerManagerTemplate?.filter(
                        template => template.id === association.to
                      )?.[0].name
                    }
                  </Typography>
                  <Button
                    label="Edit Association"
                    style="secondary"
                    size="small"
                    icon={Edit}
                    onClick={() => {
                      setEditAssociationDetails({
                        from: editRecordId,
                        association,
                      });
                      setIsTemplateDetailsModal(false);
                      setIsCreateAssociationTemplate(true);
                    }}
                  />
                </div>
              ))}
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default ProviderManager;
